/**
 * Analytics module for Ingrediable.
 *
 * This provides a thin abstraction over analytics providers.
 * Currently supports PostHog. To enable, set VITE_POSTHOG_KEY in your .env.
 *
 * Usage:
 *   import { analytics } from "./lib/analytics";
 *   analytics.track("item_added", { category: "Dairy" });
 *   analytics.identify(userId, { email });
 */

interface AnalyticsProvider {
  init(): void;
  track(event: string, properties?: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  page(name?: string): void;
  reset(): void;
}

class PostHogProvider implements AnalyticsProvider {
  private posthog: any = null;

  init() {
    const key = (import.meta as any).env?.VITE_POSTHOG_KEY;
    const host = (import.meta as any).env?.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
    if (!key) {
      console.debug("[Analytics] PostHog key not set — analytics disabled.");
      return;
    }
    import("posthog-js").then((mod) => {
      this.posthog = mod.default;
      this.posthog.init(key, {
        api_host: host,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
      });
      console.debug("[Analytics] PostHog initialized.");
    }).catch(() => {
      console.debug("[Analytics] PostHog not installed — run: npm install posthog-js");
    });
  }

  track(event: string, properties?: Record<string, unknown>) {
    this.posthog?.capture(event, properties);
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    this.posthog?.identify(userId, traits);
  }

  page(name?: string) {
    this.posthog?.capture("$pageview", name ? { page: name } : undefined);
  }

  reset() {
    this.posthog?.reset();
  }
}

class NoopProvider implements AnalyticsProvider {
  init() {}
  track() {}
  identify() {}
  page() {}
  reset() {}
}

// Export singleton
const provider = typeof window !== "undefined" ? new PostHogProvider() : new NoopProvider();

export const analytics = {
  init: () => provider.init(),
  track: (event: string, properties?: Record<string, unknown>) => provider.track(event, properties),
  identify: (userId: string, traits?: Record<string, unknown>) => provider.identify(userId, traits),
  page: (name?: string) => provider.page(name),
  reset: () => provider.reset(),
};
